'use client';

import { Bike, UserBike } from 'models/shared';
import { useState } from 'react';
import { MotionDiv } from 'motion';
import { CheckmarkIcon } from '@c/icons';
import { CategoryPageSideFilters } from '@c/CategoryOrSubcategoryPage';
import Button from './Button';
import MobileAddBikeModal from '@c/modals/MobileAddBikeModal';
import { useAuth } from 'context/AuthContext';
import SafeImage from './SafeImage';
import { Brand } from '@c/ShopByManufacturer';
import { getBrandAssets } from '@c/ShopByManufacturer';

interface ShopYourGarageProps {
  onBikeComplete: (bike: UserBike | null) => void;
  sideFilters: CategoryPageSideFilters;
  handleFilterRemove: (filter: { key: string; value: any }) => void;
}

export default function ShopYourGarage({
  onBikeComplete,
  sideFilters,
}: ShopYourGarageProps) {
  const { userDoc } = useAuth();
  const [bikeModalOpen, setBikeModalOpen] = useState(false);

  const selectedBike = {
    year: sideFilters.bikeYears?.[0]
      ? parseInt(sideFilters.bikeYears[0] as string)
      : undefined,
    make: sideFilters.bikeMakes?.[0] || '',
    model: sideFilters.bikeModels?.[0] || '',
  };
  const handleBikeComplete = (bike: UserBike | null) => {
    if (bike && !isBikeMatching(bike)) {
      onBikeComplete(bike);
    } else {
      onBikeComplete(null);
    }
  };

  const isBikeMatching = (bike: UserBike) => {
    return (
      selectedBike.make === bike.make &&
      selectedBike.model === bike.model &&
      selectedBike.year === bike.year
    );
  };

  const renderBikeCards = (className: string) => {
    const bikes = userDoc?.bikes || [];
    const sortedBikes = bikes.sort((a, b) => {
      // Sort by date_added in descending order
      const dateA = a.date_added ? new Date(a.date_added).getTime() : 0;
      const dateB = b.date_added ? new Date(b.date_added).getTime() : 0;
      return dateB - dateA;
    });

    return (
      <div className={className}>
        <div className="flex flex-nowrap gap-x-12">
          {sortedBikes.map((bike, i) => (
            <BikeCard
              key={`${bike.year} ${bike.make} ${bike.model} ${i}`}
              bike={bike}
              onBikeSelect={handleBikeComplete}
              isSelected={isBikeMatching(bike)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="w-full pb-4 sm:pb-0">
        <div className="mx-auto max-w-content">
          <div className="flex w-full flex-col gap-[1.6rem] bg-[#F6F6F6]  p-[2rem] sm:bg-white">
            <div className="flex w-screen items-center justify-between pr-16">
              <h1 className="text-[1.8rem] font-semibold">Find Parts for Your Bike</h1>

              {!!userDoc ? (
                <button
                  type="button"
                  className="border-none bg-inherit text-[1.8rem] font-semibold text-brand-secondary outline-none"
                >
                  Add +
                </button>
              ) : (
                <Button
                  text="Sign In"
                  type="secondary"
                  href={'/login'}
                  className="mr-8 sm:mr-0"
                />
              )}
            </div>

            {!!userDoc?.bikes?.length ? (
              <>
                {renderBikeCards(
                  'sm:scrollbar-auto flex w-full items-center gap-x-12 overflow-x-auto scrollbar-none sm:hidden'
                )}

                {renderBikeCards(
                  'hidden w-full items-center gap-x-12 overflow-x-auto sm:flex my-8 pb-2'
                )}
              </>
            ) : (
              <>
                {!!userDoc && (
                  <div className="flex h-fit flex-col items-center justify-center gap-y-12 py-12">
                    <h3 className="text-center text-[1.5rem] font-semibold">
                      You have no bikes in your garage
                    </h3>

                    <Button
                      text="Add a Bike"
                      type="secondary"
                      onClick={() => setBikeModalOpen(true)}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <MobileAddBikeModal
        isOpen={bikeModalOpen}
        onClose={() => setBikeModalOpen(false)}
      />
    </>
  );
}

const BikeCard = ({
  bike,
  onBikeSelect,
  isSelected,
}: {
  bike: UserBike;
  onBikeSelect: (bike: UserBike | null) => void;
  isSelected: boolean;
}) => {
  const toggleSelectedBike = () => {
    if (!isSelected) {
      onBikeSelect(bike);
    } else {
      onBikeSelect(null);
    }
  };
  const fallback = getBrandAssets(bike.make.toLowerCase() as Brand)?.src;
  const img = bike.imageUrl || fallback;

  return (
    <div
      className={`flex h-[8rem] w-[33.875rem] flex-shrink-0 cursor-pointer items-center justify-between gap-x-[2rem] rounded-[2rem] border px-[2rem] py-[0.5rem]  ${
        isSelected
          ? 'border-brand-secondary bg-red-100/40'
          : ' border-zinc-200 bg-white'
      }`}
      onClick={toggleSelectedBike}
    >
      {img && (
        <SafeImage
          src={img}
          fallbackSrc={fallback}
          alt={`${bike.make} ${bike.model}`}
          className="h-[7rem] w-[7rem]"
        />
      )}
      <div className="flex grow flex-col gap-y-2">
        <p className="text-[1.75rem] font-semibold text-black">{bike.make}</p>
        <p className="text-[1.5rem] font-medium text-[#444444]">{bike.model}</p>
      </div>

      <BikeCheckbox
        isSelected={isSelected}
        toggleSelectedBike={toggleSelectedBike}
      />
    </div>
  );
};

const BikeCheckbox = ({
  isSelected,
  toggleSelectedBike,
}: {
  isSelected: boolean;
  toggleSelectedBike: () => void;
}) => {
  return !isSelected ? (
    <MotionDiv
      className="h-[2.5rem] w-[2.5rem] rounded-full border border-zinc-300"
      onClick={(e) => {
        e.stopPropagation();
        toggleSelectedBike();
      }}
      initial={{ scale: 0.9, opacity: 0.5 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.2 }}
    />
  ) : (
    <MotionDiv
      className="flex h-[2.5rem] w-[2.5rem] items-center justify-center rounded-full bg-brand-secondary"
      onClick={(e) => {
        e.stopPropagation();
        toggleSelectedBike();
      }}
      initial={{ scale: 0.9, opacity: 0.5 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <CheckmarkIcon variant="white" height={10} width={10} />
    </MotionDiv>
  );
};
